import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Menu,
  Flex,
  Text,
  Stack,
  Center,
  Button,
  Heading,
  MenuItem,
  MenuList,
  MenuButton,
} from '@chakra-ui/react';

import { HiDocumentAdd } from 'react-icons/hi';
import { MdArrowDropDown, MdUploadFile } from 'react-icons/md';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { exportCsvItems, fetchItems } from 'api/items';

import { useWashHubListener } from 'hooks';

import { washHub } from 'store/slices';

import { CSVLink } from 'react-csv';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';
import { UserPill } from 'components/core/UserPill';
import { Filters } from 'components/primitives/Filters';
import { StatCards } from 'components/primitives/StatCards';
import { DataTable } from 'components/modules/DataTable';
import { AddItemModal } from 'components/modules/AddItemModal';
import { EditItemModal } from 'components/modules/EditItemModal';
import { UploadCSVModal } from 'components/modules/UploadCSVModal';
import { AddManualItemModal } from 'components/modules/AddManualItemModal';

import { Item } from 'types/items';
import { ItemsViewProps } from './Items.props';
import { buildItemColumns } from './itemsColumns';

const ItemsView: FC<ItemsViewProps> = ({ compType, embedded }) => {
  const queryClient = useQueryClient();

  const [urlRecord, setUrlRecord] = useState({});
  const addItemModalRef = useRef<any>(null);
  const editItemModalRef = useRef<any>(null);
  const addManualItemModalRef = useRef<any>(null);

  const csvRef = useRef<any>(null);
  const uploadCSVModalRef = useRef<any>(null);

  const queryUrl = useQueryParams();

  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washhubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const isUpdated = useUrlSharing(urlRecord, washhubId);

  const [page, setPage] = useState<number>(1);
  const [sku, setSku] = useState<string[]>();
  const [venue, setVenue] = useState<string[]>();
  const [status, setStatus] = useState<undefined | number[]>(undefined);
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const [type, setType] = useState<string>();

  const {
    mutate: mutateCsv,
    data: csvData = '',
    isLoading: isLoadingCSV,
  } = useMutation(
    async () => {
      const response = await exportCsvItems({
        sku,
        venue,
        washHub: washhubId,
        status,
      });

      return response;
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.link?.click?.();
        }, 500);
      },
    },
  );

  useEffect(() => {
    const urlParams = {
      washhubId: String(washhubId),
      ...(compType ? { compType } : {}),
      ...(type ? { type } : {}),
      ...(venue && venue.length ? { venue: venue.join('-') } : {}),
      ...(status && status.length ? { status: status.join('-') } : {}),
      ...(sku && sku.length ? { sku: sku.join('-') } : {}),
    };
    setUrlRecord(urlParams);
  }, [washhubId, compType, status, venue, sku, type]);

  useEffect(() => {
    const { status, venue, sku, type } = queryUrl;
    status && setStatus(status.split('-').map((el) => +el));
    venue && setVenue(venue.split('-'));
    sku && setSku(sku.split('-'));
    type && setType(type);
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  const queryKey = [`fetch-items-${washhubId}`, page, status, sku, venue];

  const queryFn = () =>
    fetchItems({
      sku,
      venue,
      page,
      limit: 10,
      washHub: washhubId,
      status,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
    refetchOnMount: 'always' as any,
  };

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  const invalidateQueries = () => {
    queryClient.invalidateQueries(`fetch-items-${washhubId}`);

    queryClient.invalidateQueries(`fetch-items-status-count-${washhubId}`, {
      exact: true,
    });
  };

  useWashHubListener(() => {
    setPage(1);
    setStatus(undefined);
    setSku(undefined);
    setVenue(undefined);
  });

  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const getItemsByWashHub = () => {
    const items =
      washhubId === ''
        ? data?.items
        : data?.items.filter((i) => washhubId === i.washHub._id);
    return items;
  };

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const onApplyFilters = (data: string[], type: string) => {
    setPage(1);
    setType(type);
    if (type === 'sku') {
      setSku(data);
    }
    if (type === 'site') {
      setVenue(data);
    }
  };

  const onClearFilters = () => {
    setPage(1);
    setType(undefined);
    setSku(undefined);
    setVenue(undefined);
  };

  const onUpdateClick = () => {
    setPage(1);
    setSku(undefined);
    setVenue(undefined);
    setStatus(undefined);

    invalidateQueries();
  };

  const columns = useMemo(() => buildItemColumns(), []);

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isLoadingCSV}
          ml={2}
          minW="200px"
          onClick={() => {
            mutateCsv();
          }}>
          Export Items to CSV
        </Button>
        {!isLoadingCSV && Boolean(csvData) && (
          <CSVLink
            data={csvData}
            ref={csvRef}
            filename={`washhub-${selectedWashHub?.name}-items.csv`}
            target="_blank"
          />
        )}
      </>
    );
  };

  if (!isUrlMapped) return null;

  return (
    <Flex
      direction="column"
      flex={1}
      color="support.500"
      overflow={embedded ? undefined : 'auto'}>
      <Flex alignItems="baseline" justify="space-between">
        {embedded ? <Box /> : <Heading fontSize="24px">Items</Heading>}
        <Stack isInline>
          {renderExpBtn()}
          <Button
            w="200px"
            leftIcon={<MdUploadFile size="20px" />}
            onClick={() => {
              uploadCSVModalRef?.current?.onOpen?.();
            }}>
            Upload CSV
          </Button>
          <Menu>
            <MenuButton
              isDisabled={washhubId === ''}
              as={Button}
              w="200px"
              leftIcon={<HiDocumentAdd size="20px" />}
              rightIcon={<MdArrowDropDown size="20px" />}>
              Create Item
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => {
                  addItemModalRef?.current?.onOpen?.();
                }}>
                EPC
              </MenuItem>
              <MenuItem
                onClick={() => {
                  addManualItemModalRef?.current?.onOpen?.();
                }}>
                Non EPC
              </MenuItem>
            </MenuList>
          </Menu>

          {!embedded && <UserPill />}
        </Stack>
      </Flex>
      <AddItemModal ref={addItemModalRef} />
      <EditItemModal ref={editItemModalRef} />
      <AddManualItemModal ref={addManualItemModalRef} />
      <UploadCSVModal type={'item'} ref={uploadCSVModalRef} />
      <Flex h={embedded ? '20px' : '50px'} />
      <StatCards
        type={`items`}
        selected={status}
        onSelectStatus={(_selected) => {
          if (Array.isArray(_selected)) {
            setPage(1);
            setStatus(_selected);
          }
        }}
      />
      <Flex direction="column" position="relative">
        <Flex>
          <Filters
            venue={type ? (type == 'sku' ? sku : venue) : []}
            isFullButton
            onApplyFilters={onApplyFilters}
            onClearFilters={onClearFilters}
          />
          <Flex h="10px" w="20px" />
          <Button
            w="180px"
            borderRadius="5px"
            onClick={onUpdateClick}
            isLoading={isFetching}>
            Update Table
          </Button>
        </Flex>
        <DataTable<Item>
          mt={0}
          minW="2000px"
          data={getItemsByWashHub() || []}
          columns={columns}
          isLoading={isLoading || isFetching}
          pagination={pagination}
          onRowClick={(item) => {
            editItemModalRef?.current?.onOpen?.(item);
          }}
          renderEmpty={() => (
            <Center p={8}>
              <Text fontSize="20px">No Items available</Text>
            </Center>
          )}
        />
      </Flex>
    </Flex>
  );
};

export default ItemsView;
